<template>
  <v-card variant="flat" class="gm-card" rounded="xl" :loading="loading">
    <template #title>
      <div class="d-flex align-center justify-lg-space-between">
        <span>待办事项</span>

        <div class="d-flex align-center">
          <div class="d-flex align-center ga-2 task-tabs" :class="{ 'task-tabs--disabled': loading }">
            <template v-for="(tab, i) in visibleTabs" :key="tab.value">
              <span v-ripple="{ class: 'text-indigo-lighten-4' }" class="task-tab"
                :class="{ active: currentTab === tab.value }" @click="onTabClick(tab.value)">{{
                  tab.label }}</span>
              <v-divider vertical v-if="i < visibleTabs.length - 1"></v-divider>
            </template>
          </div>
          <div class="ml-4" v-if="checkPermi(['workbench:historyTodo:query'])"><v-btn variant="text"
              color="var(--color-primary)" append-icon="mdi-chevron-right" rounded :disabled="loading"
              @click="drawer = true">历史待办记录</v-btn>
          </div>
        </div>
      </div>

      <!-- 待办事项列表（通知/日报/告警同一套布局与样式） -->
      <div class="gm-border pa-4 rounded mt-4">
        <div v-if="!loading && !displayTodoList.length" class="task-empty text-medium-emphasis">
          暂无数据
        </div>
        <el-carousel v-else :interval="5000" height="208px" direction="horizontal" arrow="never">
          <el-carousel-item v-for="(tasks, gi) in groupedTasks" :key="gi">
            <div class="task-item" v-for="task in tasks" :key="todoId(task) || task.datetime">
              <div class="task-icon">
                <img :src="getTaskIcon(task.type)" alt="">
              </div>
              <div class="task-title">{{ labelFromValue(task.type) }}</div>
              <div class="task-station text-grey-darken-2" v-if="task.name">{{ (task.name ? task.name + ':' : '') }}
              </div>
              <div class="task-info text-grey-darken-2">{{ task.message }}</div>
              <div class="task-time">{{ task.datetime }}</div>
              <div class="task-actions">
                <v-btn variant="text" color="var(--color-primary)" rounded @click.stop="onHandleTask(task)">详情</v-btn>
              </div>
            </div>
          </el-carousel-item>
        </el-carousel>
      </div>
    </template>

    <!-- 历史待办事项列表 -->
    <HistoryTasks v-model="drawer" />

    <!-- 告警详情弹窗 -->
    <AlarmDialog v-model="alarmDialog" :task="getActiveData(2)" @transferOrder="fetchOrderList" />

    <!-- 日报详情弹窗 -->
    <DailyReport v-model:detailsVisible="dailyReportDialog" :task="getActiveData(1)" />

    <!-- 运维诊断弹窗 -->
    <AlarmReportDrawer v-model="alarmReportDialog" :report-id="getActiveData(5).messageId" />
  </v-card>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getTodoList, switchTodoReadStatus } from '@/api/workbench/index.js'
import HistoryTasks from '../HistoryTasks/index.vue'
import dayjs from 'dayjs'
import AlarmDialog from './AlarmDialog.vue'
import AlarmReportDrawer from './AlarmReportDrawer.vue'
import DailyReport from '../details.vue'
import todoAlarmIcon from '@/assets/icons/svg/todo-alarm.svg'
import todoApplicationIcon from '@/assets/icons/svg/todo-application.svg'
import todoWarningIcon from '@/assets/icons/svg/todo-warning.svg'
import todoReportIcon from '@/assets/icons/svg/toto-report.svg'
import { useRouter } from 'vue-router'
import { checkPermi } from '@/utils/permission'

const router = useRouter()
const drawer = ref(false)
const alarmDialog = ref(false)
const dailyReportDialog = ref(false)
const alarmReportDialog = ref(false)


const props = defineProps({
  fetchOrderList: {
    type: Function,
    default: () => { }
  }
})

// 类型：通知0，日报1，告警2，提醒3，申请4，运维诊断5
const currentTab = ref(1)
const Tabs = [
  {
    label: '日报',
    value: 1,
    perms: ['workbench:dailyReport:query']
  },
  {
    label: '运维诊断',
    value: 5,
    perms: ['workbench:alarmReport:query']
  },
  {
    label: '告警',
    value: 2,
    perms: ['workbench:alarm:query']
  },
  {
    label: '通知',
    value: 0,
    perms: ['workbench:notice:query']
  },
]

/** 须先于依赖它的 computed 声明，否则「Cannot access 'todoList' before initialization」 */
const loading = ref(false)
const todoList = ref([])

function hasTabPermission(tab) {
  if (!Array.isArray(tab.perms) || !tab.perms.length) return true
  return checkPermi(tab.perms)
}

const visibleTabs = computed(() => Tabs.filter(hasTabPermission))

watch(visibleTabs, (list) => {
  if (!list.length) return
  if (!list.some(tab => tab.value === currentTab.value)) {
    onTabClick(list[0].value)
  }
}, { flush: 'post' })

function labelFromValue(value) {
  const map = { 0: '通知', 1: '日报', 2: '告警', 3: '提醒', 4: '申请', 5: '运维诊断' }
  return map[value] || ''
}

function getTaskIcon(type) {
  const iconMap = {
    0: todoWarningIcon,
    1: todoReportIcon,
    2: todoAlarmIcon,
    3: todoWarningIcon,
    4: todoApplicationIcon,
    5: todoReportIcon,
  }
  return iconMap[type] || todoReportIcon
}

/** 与当前 Tab 一致的列表（切换 Tab 后、新数据返回前可为空，避免串类型） */
const displayTodoList = computed(() => {
  const tab = currentTab.value
  return todoList.value.filter((item) => Number(item?.type) === Number(tab))
})

const groupedTasks = computed(() => groupBySize(displayTodoList.value))

function onTabClick(tab) {
  currentTab.value = tab
  fetchTodoList(undefined, tab)
}

const activeData = ref({})
function isUnread(todo) {
  return todo?.isRead !== true
}
function todoId(todo) {
  return todo?.id ?? todo?.todoId
}

async function markTodoRead(todo) {
  if (!isUnread(todo)) return
  const id = todoId(todo)
  if (!id) return
  await switchTodoReadStatus(id, { state: 2 })
  todo.isRead = true
}
function setActiveData(type, data) {
  activeData.value[type] = data
}
function getActiveData(type) {
  return activeData.value[type] || {}
}

async function onHandleTask(task) {
  await markTodoRead(task)
  setActiveData(task.type, task)

  const t = Number(task.type)
  if (t === 0) {
    const url = task.detailInfo
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
    return
  }
  if (t === 2) {
    alarmDialog.value = true
  }
  else if (t === 1) {
    dailyReportDialog.value = true
  }
  else if (t === 4) {
    router.push({
      path: '/approve',
      query: {
        id: task.id,
        // stationId: task.stationId
      }
    })
  }
  else if (t === 5) {
    alarmReportDialog.value = true
  }
}

function groupBySize(list = [], size = 4) {
  const result = []
  for (let i = 0; i < list.length; i += size) {
    result.push(list.slice(i, i + size))
  }
  return result
}

async function fetchTodoList(state, type) {
  const startTime = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss')
  const endTime = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')

  const params = {
    pageNum: 1,
    pageSize: 999,
    startTime,
    endTime,
  }
  if (state != null) params.state = state
  if (type != null) params.type = type

  loading.value = true

  if (type === 0) {
    params.startTime = dayjs().subtract(1, 'month').startOf('day').format('YYYY-MM-DD HH:mm:ss')
    params.endTime = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
  }

  try {
    const res = await getTodoList(params)
    console.log('res', res)
    todoList.value = res.data.list || []
  } catch (error) {
    console.log(error)
  } finally {
    loading.value = false
  }
}

fetchTodoList(undefined, 1)

</script>
<style scoped lang="scss">
.task-tabs {
  font-size: 14px;
  font-weight: normal;
  font-family: 'HarmonyOS Sans SC';
}

.task-tabs--disabled {
  pointer-events: none;
  filter: grayscale(0.5) opacity(0.5);
}

.task-tab {
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
}

.task-tab.active {
  color: var(--color-primary);
}

.task-item {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 16px;
  font-weight: normal;
  padding-left: 16px;
  height: 48px;
  font-size: 14px;
}

.task-item:hover {
  background: linear-gradient(90deg, rgba(38, 85, 241, 0.04) 0%, rgba(85, 7, 253, 0.04) 100%);
}

.task-item+.task-item {
  border-top: 1px solid #edf2f6;
}

.task-empty {
  min-height: 208px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
}

.task-icon {
  display: flex;
  align-items: center;
}

.task-time {
  color: #2E2E2E80;
  margin-left: auto;
}

.task-title {
  font-weight: 500;
}

.task-info {
  font-weight: 400;
}

:deep(.el-carousel__button) {
  background-color: var(--color-primary);
  width: 20px;
  height: 3px;
  border-radius: 2px;
}

:deep(.el-carousel__indicators--horizontal) {
  bottom: -16px;
  margin: 0;
  padding: 0;
  row-gap: 0;
}
</style>
